export function utmCrs(zone, hemisphere = 'S') {
  const normalizedZone = Math.max(1, Math.min(60, Math.trunc(Number(zone) || 50)));
  const normalizedHemisphere = String(hemisphere).toUpperCase() === 'N' ? 'N' : 'S';
  const epsg = (normalizedHemisphere === 'N' ? 32600 : 32700) + normalizedZone;
  return { type: 'utm', datum: 'WGS84', zone: normalizedZone, hemisphere: normalizedHemisphere, epsg, name: `WGS 84 / UTM ${normalizedZone}${normalizedHemisphere}`, unit: 'metre', status: 'resolved' };
}

export function latLonToUtm(latitude, longitude, requestedZone = null, requestedHemisphere = null) {
  const a = 6378137, eccSquared = 0.00669438, k0 = 0.9996;
  const naturalZone = Math.floor((longitude + 180) / 6) + 1;
  const zone = requestedZone == null ? naturalZone : Math.max(1, Math.min(60, Math.trunc(Number(requestedZone))));
  const lonOrigin = (zone - 1) * 6 - 180 + 3;
  const latRad = latitude * Math.PI / 180, lonRad = longitude * Math.PI / 180, lonOriginRad = lonOrigin * Math.PI / 180;
  const eccPrimeSquared = eccSquared / (1 - eccSquared);
  const N = a / Math.sqrt(1 - eccSquared * Math.sin(latRad) ** 2);
  const T = Math.tan(latRad) ** 2, C = eccPrimeSquared * Math.cos(latRad) ** 2, A = Math.cos(latRad) * (lonRad - lonOriginRad);
  const M = a * ((1 - eccSquared / 4 - 3 * eccSquared ** 2 / 64 - 5 * eccSquared ** 3 / 256) * latRad
    - (3 * eccSquared / 8 + 3 * eccSquared ** 2 / 32 + 45 * eccSquared ** 3 / 1024) * Math.sin(2 * latRad)
    + (15 * eccSquared ** 2 / 256 + 45 * eccSquared ** 3 / 1024) * Math.sin(4 * latRad)
    - (35 * eccSquared ** 3 / 3072) * Math.sin(6 * latRad));
  const easting = k0 * N * (A + (1 - T + C) * A ** 3 / 6 + (5 - 18 * T + T ** 2 + 72 * C - 58 * eccPrimeSquared) * A ** 5 / 120) + 500000;
  let northing = k0 * (M + N * Math.tan(latRad) * (A ** 2 / 2 + (5 - T + 9 * C + 4 * C ** 2) * A ** 4 / 24 + (61 - 58 * T + T ** 2 + 600 * C - 330 * eccPrimeSquared) * A ** 6 / 720));
  const hemisphere = requestedHemisphere == null ? (latitude < 0 ? 'S' : 'N') : (String(requestedHemisphere).toUpperCase() === 'N' ? 'N' : 'S');
  if (hemisphere === 'S') northing += 10000000;
  return { easting, northing, zone, hemisphere, naturalZone, naturalHemisphere: latitude < 0 ? 'S' : 'N' };
}
export function utmToLatLon(easting, northing, zone, hemisphere) {
  const a = 6378137;
  const eccSquared = 0.00669438;
  const k0 = 0.9996;
  const eccPrimeSquared = eccSquared / (1 - eccSquared);
  const e1 = (1 - Math.sqrt(1 - eccSquared)) / (1 + Math.sqrt(1 - eccSquared));

  let y = northing;
  if (String(hemisphere).toUpperCase() === 'S') {
    y -= 10000000;
  }

  const M = y / k0;
  const mu = M / (a * (1 - eccSquared / 4 - 3 * Math.pow(eccSquared, 2) / 64 - 5 * Math.pow(eccSquared, 3) / 256));

  const phi1Rad = mu + (3 * e1 / 2 - 27 * Math.pow(e1, 3) / 32) * Math.sin(2 * mu)
    + (21 * Math.pow(e1, 2) / 16 - 55 * Math.pow(e1, 4) / 32) * Math.sin(4 * mu)
    + (151 * Math.pow(e1, 3) / 96) * Math.sin(6 * mu);

  const N1 = a / Math.sqrt(1 - eccSquared * Math.pow(Math.sin(phi1Rad), 2));
  const T1 = Math.pow(Math.tan(phi1Rad), 2);
  const C1 = eccPrimeSquared * Math.pow(Math.cos(phi1Rad), 2);
  const R1 = a * (1 - eccSquared) / Math.pow(1 - eccSquared * Math.pow(Math.sin(phi1Rad), 2), 1.5);
  const D = (easting - 500000) / (N1 * k0);

  let latRad = phi1Rad - (N1 * Math.tan(phi1Rad) / R1) * (
    Math.pow(D, 2) / 2
    - (5 + 3 * T1 + 10 * C1 - 4 * Math.pow(C1, 2) - 9 * eccPrimeSquared) * Math.pow(D, 4) / 24
    + (61 + 90 * T1 + 298 * C1 + 45 * Math.pow(T1, 2) - 252 * eccPrimeSquared - 3 * Math.pow(C1, 2)) * Math.pow(D, 6) / 720
  );

  let lonRad = (
    D
    - (1 + 2 * T1 + C1) * Math.pow(D, 3) / 6
    + (5 - 2 * C1 + 28 * T1 - 3 * Math.pow(C1, 2) + 8 * eccPrimeSquared + 24 * Math.pow(T1, 2)) * Math.pow(D, 5) / 120
  ) / Math.cos(phi1Rad);

  const lonOrigin = (zone - 1) * 6 - 180 + 3;
  let longitude = (lonRad * 180 / Math.PI) + lonOrigin;
  let latitude = latRad * 180 / Math.PI;

  // Formula inverse UTM di atas sangat cepat dan akurat di dalam zona
  // normal. Maply juga dapat menampilkan data regional yang sementara
  // diproyeksikan ke satu Project CRS; jauh dari meridian tengah, deret
  // inverse tersebut dapat menyisakan error puluhan meter. Satu langkah
  // Newton mengikat hasil inverse ke formula forward yang Maply gunakan,
  // tanpa mengubah EPSG atau menebak zona baru.
  const projected = latLonToUtm(
    latitude,
    longitude,
    zone,
    hemisphere
  );
  const errorEasting = projected.easting - easting;
  const errorNorthing = projected.northing - northing;

  if (
    Number.isFinite(errorEasting) &&
    Number.isFinite(errorNorthing) &&
    Math.hypot(errorEasting, errorNorthing) > 0.0001
  ) {
    const deltaDegrees = 1e-6;
    const latitudeStep = latLonToUtm(
      latitude + deltaDegrees,
      longitude,
      zone,
      hemisphere
    );
    const longitudeStep = latLonToUtm(
      latitude,
      longitude + deltaDegrees,
      zone,
      hemisphere
    );
    const dEastingDLatitude =
      (latitudeStep.easting - projected.easting) / deltaDegrees;
    const dNorthingDLatitude =
      (latitudeStep.northing - projected.northing) / deltaDegrees;
    const dEastingDLongitude =
      (longitudeStep.easting - projected.easting) / deltaDegrees;
    const dNorthingDLongitude =
      (longitudeStep.northing - projected.northing) / deltaDegrees;
    const determinant =
      dEastingDLatitude * dNorthingDLongitude -
      dEastingDLongitude * dNorthingDLatitude;

    if (Number.isFinite(determinant) && Math.abs(determinant) > 1e-9) {
      const latitudeCorrection =
        (
          dNorthingDLongitude * errorEasting -
          dEastingDLongitude * errorNorthing
        ) / determinant;
      const longitudeCorrection =
        (
          -dNorthingDLatitude * errorEasting +
          dEastingDLatitude * errorNorthing
        ) / determinant;
      latitude -= latitudeCorrection;
      longitude -= longitudeCorrection;
    }
  }

  return { latitude, longitude };
}
